'use client'

import { useEffect } from 'react'
import { logClientError } from '@/lib/logError'
import { clientDiagnostics } from '@/lib/clientErrors'

// Mounts in the root layout to capture unhandled JS errors and promise
// rejections that don't go through React's error boundary (e.g. async callbacks,
// third-party script failures). Renders nothing.
export function ErrorReporter() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logClientError({
        source: 'window_error',
        message: event.message,
        stack: event.error?.stack,
        url: window.location.href,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          ...clientDiagnostics(),
        },
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      logClientError({
        source: 'unhandled_rejection',
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
        url: window.location.href,
        metadata: clientDiagnostics(),
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return null
}
